export enum RoleType {
    'ADMIN' = 'ADMIN',
    'AGENCY_ADMIN' = 'AGENCY_ADMIN',
    'MANAGER' = 'MANAGER',
    'MANAGER_ADMIN_ALPV' = 'MANAGER_ADMIN_ALPV',
    'SUBSCRIBER' = 'SUBSCRIBER',
    'NATIONAL_MANAGER' = 'NATIONAL_MANAGER',
    'LGCE' = 'LGCE',
}

export enum PermissionType {
    'createUser' = 'createUser',
    'updateUser' = 'updateUser',
    'deleteUser' = 'deleteUser',
    'viewUser' = 'viewUser',
    'acceptTos' = 'acceptTos',

    'createTag' = 'createTag',
    'updateTag' = 'updateTag',
    'deleteTag' = 'deleteTag',
    'viewTag' = 'viewTag',
    'viewTagsTable' = 'viewTagsTable',

    'createSubscription' = 'createSubscription',
    'updateSubscription' = 'updateSubscription',
    'deleteSubscription' = 'deleteSubscription',
    'viewSubscription' = 'viewSubscription',

    'createDevice' = 'createDevice',
    'updateDevice' = 'updateDevice',
    'deleteDevice' = 'deleteDevice',
    'viewDevice' = 'viewDevice',

    'createAgency' = 'createAgency',
    'updateAgency' = 'updateAgency',
    'deleteAgency' = 'deleteAgency',
    'viewAgency' = 'viewAgency',
    'manageAllAgencies' = 'manageAllAgencies',

    'createAlert' = 'createAlert',
    'viewAlert' = 'viewAlert',

    'createBreadcrumb' = 'createBreadcrumb',
    'viewBreadcrumb' = 'viewBreadcrumb',
    'updateBreadcrumb' = 'updateBreadcrumb',

    'createTechnicalMessage' = 'createTechnicalMessage',
    'updateTechnicalMessage' = 'updateTechnicalMessage',
    'deleteTechnicalMessage' = 'deleteTechnicalMessage',
    'viewTechnicalMessage' = 'viewTechnicalMessage',

    'useWebApp' = 'useWebApp',

    'notifyCGUUpdate' = 'notifyCGUUpdate',
    'sendPushNotifs' = 'sendPushNotifs',

    'viewPvLog' = 'viewPvLog',

    'viewSmsBackupPublicKey' = 'viewSmsBackupPublicKey',

    'updatePushNotificationToken' = 'updatePushNotificationToken',

    'execScripts' = 'execScripts',
}

export const permissionsLike = {};

interface RoleDefinition {
    label: string;
    pluralLabel?: string;
    creationLabel?: string;
    updateLabel?: string;
    extends?: RoleType[];
    permissions?: PermissionType[];
    canDeleteUsersWithRoles?: RoleType[];
    canCreateUsersWithRoles?: RoleType[];
    canViewUsersWithRoles?: RoleType[];
}

export const roles: { [role in RoleType]: RoleDefinition } = {
    [RoleType.SUBSCRIBER]: {
        label: 'Abonné',
        pluralLabel: 'Abonnés',
        creationLabel: 'Nouvel abonné',
        updateLabel: 'Mise à jour abonné',
        permissions: [
            PermissionType.createAlert,
            PermissionType.createBreadcrumb,
            PermissionType.updateBreadcrumb,
            PermissionType.createTechnicalMessage,

            // Allowed to check if an ongoing breadcrumb exists
            PermissionType.viewBreadcrumb,

            PermissionType.acceptTos,

            // Needed to sync user's own information
            PermissionType.viewUser,

            PermissionType.viewSmsBackupPublicKey,

            PermissionType.updatePushNotificationToken,
        ],
        canViewUsersWithRoles: [
            RoleType.SUBSCRIBER,
        ],
    },
    [RoleType.MANAGER]: {
        label: 'Gestionnaire',
        pluralLabel: 'Gestionnaires',
        creationLabel: 'Nouveau gestionnaire',
        updateLabel: 'Mise à jour gestionnaire',
        permissions: [
            PermissionType.viewUser,
            PermissionType.createUser,
            PermissionType.updateUser,

            PermissionType.createSubscription,
            PermissionType.updateSubscription,
            PermissionType.deleteSubscription,
            PermissionType.viewSubscription,

            PermissionType.createDevice,
            PermissionType.updateDevice,
            PermissionType.deleteDevice,
            PermissionType.viewDevice,
            PermissionType.useWebApp,
            PermissionType.viewAlert,
            PermissionType.viewBreadcrumb,
            PermissionType.updateBreadcrumb,
            PermissionType.viewTechnicalMessage,
            PermissionType.viewTag,
        ],
        canCreateUsersWithRoles: [
            RoleType.SUBSCRIBER,
        ],
        canViewUsersWithRoles: [
            RoleType.SUBSCRIBER,
        ],
    },
    [RoleType.MANAGER_ADMIN_ALPV]: {
        label: 'Gestionnaire administrateur ALPV',
        pluralLabel: 'Gestionnaires administrateurs ALPV',
        creationLabel: 'Nouveau gestionnaire administrateur ALPV',
        updateLabel: 'Mise à jour gestionnaire administrateur ALPV',
        extends: [RoleType.MANAGER],
        permissions: [
            PermissionType.viewPvLog,
            PermissionType.deleteUser,
        ],
        canDeleteUsersWithRoles: [
            RoleType.MANAGER,
            RoleType.SUBSCRIBER,
        ],
        canCreateUsersWithRoles: [
            RoleType.SUBSCRIBER,
            RoleType.MANAGER,
        ],
        canViewUsersWithRoles: [
            RoleType.SUBSCRIBER,
            RoleType.MANAGER,
        ],
    },
    [RoleType.NATIONAL_MANAGER]: {
        label: 'Gestionnaire national',
        pluralLabel: 'Gestionnaires nationaux',
        creationLabel: 'Nouveau gestionnaire national',
        updateLabel: 'Mise à jour gestionnaire national',
        extends: [RoleType.MANAGER],
        permissions: [
            PermissionType.viewAgency,
            PermissionType.createAgency,
            PermissionType.updateAgency,
            PermissionType.manageAllAgencies,
            PermissionType.sendPushNotifs,
            PermissionType.deleteUser,
        ],
        canDeleteUsersWithRoles: [
            RoleType.MANAGER,
            RoleType.SUBSCRIBER,
            RoleType.MANAGER_ADMIN_ALPV,
            RoleType.AGENCY_ADMIN,
        ],
        canCreateUsersWithRoles: [
            RoleType.MANAGER,
            RoleType.SUBSCRIBER,
            RoleType.MANAGER_ADMIN_ALPV,
            RoleType.AGENCY_ADMIN,
        ],
        canViewUsersWithRoles: [
            RoleType.MANAGER,
            RoleType.SUBSCRIBER,
            RoleType.MANAGER_ADMIN_ALPV,
            RoleType.AGENCY_ADMIN,
        ],
    },
    [RoleType.AGENCY_ADMIN]: {
        label: 'Administrateur d\'agence',
        pluralLabel: 'Administrateurs d\'agence',
        creationLabel: 'Nouvel administrateur d\'agence',
        updateLabel: 'Mise à jour administrateur d\'agence',
        extends: [RoleType.MANAGER],
        permissions: [
            PermissionType.deleteUser,

            PermissionType.createTag,
            PermissionType.updateTag,
            PermissionType.deleteTag,
            PermissionType.viewTagsTable,
        ],
        canDeleteUsersWithRoles: [
            RoleType.MANAGER,
            RoleType.SUBSCRIBER,
            RoleType.MANAGER_ADMIN_ALPV,
        ],
        canCreateUsersWithRoles: [
            RoleType.MANAGER,
            RoleType.SUBSCRIBER,
            RoleType.MANAGER_ADMIN_ALPV,
        ],
        canViewUsersWithRoles: [
            RoleType.MANAGER,
            RoleType.SUBSCRIBER,
            RoleType.MANAGER_ADMIN_ALPV,
            RoleType.AGENCY_ADMIN,
        ],
    },
    [RoleType.ADMIN]: {
        label: 'Admin',
        pluralLabel: 'Administrateurs',
        creationLabel: 'Nouvel administrateur',
        updateLabel: 'Mise à jour administrateur',
    },
    [RoleType.LGCE]: {
        label: 'LGCE',
        pluralLabel: 'Profils LGCE',
        creationLabel: 'Nouveau profil LGCE',
        updateLabel: 'Mise à jour profil LGCE',
        extends: [RoleType.ADMIN],
        permissions: [
            PermissionType.execScripts,
        ],
    }
};
